import { getUnixTime, startOfDay } from 'date-fns';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { DatePicker } from 'src/components/DatePicker';
import { FormCheckbox } from 'src/shared/FormCheckbox';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';

export const Scheduling = ({
  optional = false,
  showSendNotification = false,
  type,
}: {
  optional?: boolean;
  showSendNotification?: boolean;
  type: string;
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'group.Scheduling',
  });
  const { values, setFieldValue } = useFormikContext<any>();
  const [showDelayIntervals, setShowDelayIntervals] = useState<
    '' | 'week' | 'day' | 'expires'
  >('');

  const delayOptions = useMemo(() => {
    const options = [{ value: '', label: t('Week 1 - Release Immediately') }];
    for (let i = 1; i < 200; i += 1) {
      options.push({ value: i.toString(), label: `${t('Week')} ${i + 1}` });
    }
    return options;
  }, [t]);

  const handleScheduleModeChange = (
    mode: '' | 'week' | 'day' | 'expires',
    setFieldValue: any
  ) => {
    setFieldValue('delayInterval', '');
    setFieldValue('expiresAt', '');
    setShowDelayIntervals(mode);

    if (!mode) {
      setFieldValue('delayInterval', '');
      setFieldValue('expiresAt', '');
    } else if (mode == 'day') {
      setFieldValue('delayInterval', getUnixTime(startOfDay(new Date())));
    } else if (mode == 'expires') {
      setFieldValue('expiresAt', getUnixTime(startOfDay(new Date())));
    } else if (mode == 'week') {
      setFieldValue('delayInterval', 1);
    }
  };

  useEffect(() => {
    if (values.delayInterval) {
      if (values.expiresAt) setShowDelayIntervals('expires');
      else if (!values.delayInterval) setShowDelayIntervals('');
      else if (values.delayInterval > 200) setShowDelayIntervals('day');
      else setShowDelayIntervals('week');
    }
  }, [setFieldValue, values.delayInterval, values.expiresAt]);

  return (
    <>
      <BaseSelect
        label={optional ? t('Scheduling (Optional)') : t('Scheduling')}
        options={[
          { id: '', label: t('Week 1 - Release Immediately'), value: '' },
          { id: 'week', label: t('Release on a specific week'), value: 'week' },
          { id: 'day', label: t('Schedule for a specific day'), value: 'day' },
          {
            id: 'expires',
            label: t('Schedule for members who join before a specific date'),
            value: 'expires',
          },
        ]}
        value={showDelayIntervals}
        onChange={(value) => {
          handleScheduleModeChange(value, setFieldValue);
        }}
      />
      {showDelayIntervals && (
        <Box>
          {showDelayIntervals === 'week' && (
            <FormSelect
              name="delayInterval"
              label={t(
                'Make this {{type}} available to group members starting',
                {
                  type,
                }
              )}
              options={delayOptions as any[]}
              size="medium"
              sx={{
                '& .MuiMenu-paper': {
                  maxHeight: '400px',
                },
              }}
            />
          )}
          {showDelayIntervals === 'day' && (
            <DatePicker
              label={t('Make this {{type}} available to group members on', {
                type,
              })}
              format="eeee, MMM dd, yyyy"
              value={
                values.delayInterval
                  ? new Date(values.delayInterval * 1000)
                  : null
              }
              onChange={(date) => {
                if (date) {
                  const timestamp = getUnixTime(date);
                  setFieldValue('delayInterval', timestamp);
                } else {
                  setFieldValue('delayInterval', null);
                }
              }}
            />
          )}
          {showDelayIntervals === 'expires' && (
            <DatePicker
              label={t(
                'Make this {{type}} available only to group members who join before',
                {
                  type,
                }
              )}
              format="eeee, MMM dd, yyyy"
              value={
                values.expiresAt ? new Date(values.expiresAt * 1000) : null
              }
              onChange={(date) => {
                if (date) {
                  const timestamp = Math.floor(
                    date.setHours(0, 0, 0, 0) / 1000
                  );
                  setFieldValue('expiresAt', timestamp);
                } else {
                  setFieldValue('expiresAt', null);
                }
              }}
            />
          )}
        </Box>
      )}
      {!showDelayIntervals && showSendNotification && (
        <FormCheckbox name="sendNotification" label={t('Send Notification')} />
      )}
    </>
  );
};
